import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

import { ScrollArea } from '~/DesignSystem/components/scroll-area'
import { Spinner } from '~/DesignSystem/components/spinner'

type ThreadsScrollProps = React.PropsWithChildren<{
  onTop?: () => void
  onBottom?: () => void
}>

export type ThreadsScrollRefs = {
  scroll: HTMLDivElement | null
  top: HTMLDivElement | null
  bottom: HTMLDivElement | null
}

const ThreadsScroll = forwardRef<ThreadsScrollRefs, ThreadsScrollProps>(
  ({ onTop, onBottom, children }, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const topRef = useRef<HTMLDivElement>(null)
    const bottomRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => ({
      scroll: scrollRef.current,
      top: topRef.current,
      bottom: bottomRef.current,
    }))

    useEffect(() => {
      const observers: IntersectionObserver[] = []

      if (onTop && topRef.current && topRef.current.firstElementChild) {
        const topObserver = new IntersectionObserver(
          entries => {
            if (entries[0].isIntersecting) {
              onTop()
            }
          },
          { threshold: 0.5 },
        )
        topObserver.observe(topRef.current.firstElementChild)
        observers.push(topObserver)
      }

      if (onBottom && bottomRef.current) {
        const bottomObserver = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) {
            onBottom()
          }
        })
        bottomObserver.observe(bottomRef.current)
        observers.push(bottomObserver)
      }

      return () => {
        observers.forEach(observer => observer.disconnect())
      }
    }, [onTop, onBottom])

    return (
      <ScrollArea ref={scrollRef} className="h-full w-full">
        <div className="p-4">
          {!!onTop && (
            <div ref={topRef} className="flex justify-center py-6 first:pt-0 last:pb-0">
              <Spinner className="size-6 opacity-25" />
            </div>
          )}
          {children}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
    )
  },
)

export { ThreadsScroll }
